import { SignUpField, SignUpFieldErrors } from 'utils/SignUpRules';

/**
 * Plain-language messages for what the server can answer to a sign-up or a log-in.
 *
 * The form (utils/SignUpRules.ts) is meant to stop bad details before any request; these messages cover
 * what still gets through (a rule the form cannot check exactly, or a problem on our side) so the person
 * always learns WHAT is wrong and WHERE, or that it is not their fault, instead of a generic error.
 */

export interface FriendlyAuthError {
  message: string;
  /** Set when the server rejected specific fields: the person is sent back to fix exactly those. */
  fields?: SignUpFieldErrors;
}

const FIELD_HELP: Record<SignUpField, string> = {
  firstName: 'First name isn\'t accepted. It can only contain letters, spaces and hyphens (no apostrophes, accents or numbers).',
  lastName: 'Last name isn\'t accepted. It can only contain letters, spaces and hyphens (no apostrophes, accents or numbers).',
  email: 'That email address isn\'t accepted. Check it for typos, like name@example.com.',
  gender: 'Choose an option for gender.',
  country: 'Choose your country.',
  phone: 'Phone number isn\'t accepted. Use international format, for example +233241234567.',
  companyName: 'Company name isn\'t accepted. Use 3 to 50 letters, numbers, spaces or hyphens, or leave it blank.',
  jobTitle: 'Job title isn\'t accepted. Use 3 to 50 letters, numbers, spaces or hyphens, or leave it blank.',
  password: 'Your password isn\'t accepted. It needs a capital letter, a number and one of these symbols: ! @ # $ % ^ & *',
  confirmPassword: 'The two passwords don\'t match.',
};

// Which field a server complaint is about. Order matters: "confirm password" before "password".
const FIELD_HINTS: Array<[SignUpField, RegExp]> = [
  ['confirmPassword', /confirm ?password/i],
  ['password', /password/i],
  ['firstName', /first ?name/i],
  ['lastName', /last ?name/i],
  ['companyName', /company ?name/i],
  ['jobTitle', /job ?title/i],
  ['email', /email/i],
  ['phone', /phone/i],
  ['gender', /gender/i],
  ['country', /country/i],
];

/** The field a server complaint names, if it names one. */
export const fieldFromServerDetail = (detail: string | null | undefined): SignUpField | undefined =>
  FIELD_HINTS.find(([, pattern]) => pattern.test(detail ?? ''))?.[0];

const OUR_SIDE =
  'We couldn\'t finish setting up your account, and it isn\'t anything you did. Please wait a few minutes, then try to log in with the email and password you just used. If that doesn\'t work, contact support and tell us the time you tried.';

/**
 * The message for a failed sign-up.
 *
 * @param serverMessage - The server's headline, e.g. "Validation failed." or "Email already exists."
 * @param serverDetail - The specific complaint, e.g. "First name must contain only alphabetic characters…"
 */
export const friendlySignUpError = ({ serverMessage, serverDetail }: { serverMessage: string; serverDetail?: string | null }): FriendlyAuthError => {
  const headline = serverMessage.toLowerCase();

  if (headline.includes('exists') || headline.includes('duplicate')) {
    return {
      message: 'An account with this email already exists. Log in with it, or use "Forgot password" if you don\'t remember the password. If you started signing up earlier, logging in lets you finish.',
    };
  }

  if (headline.includes('validation')) {
    const field = fieldFromServerDetail(serverDetail);
    if (field) {
      const message = FIELD_HELP[field];
      return { message, fields: { [field]: message } };
    }
    return { message: 'Some of your details weren\'t accepted. Please go back and check each field, then try again.' };
  }

  if (headline.includes('registration failed') || headline.includes('unexpected') || headline.includes('error occurred')) {
    return { message: OUR_SIDE };
  }

  return { message: 'We couldn\'t create your account just now. Please check your connection and try again in a moment. If it keeps happening, contact support.' };
};

/**
 * The message for a failed log-in. The server checks the password's format even at log-in, so a
 * "validation" answer really means the email or password isn't right; say that instead of "required fields".
 */
export const friendlyLoginError = ({ serverMessage, serverDetail }: { serverMessage: string; serverDetail?: string | null }): string => {
  const headline = serverMessage.toLowerCase();
  if (headline.includes('suspended')) return 'Your account has been suspended. Please contact our support team for more information.';
  if (headline.includes('not found')) return 'We couldn\'t find an account with that email. Check the address, or create an account.';
  if (headline.includes('incorrect')) return 'That email or password isn\'t correct. Try again, or use "Forgot password".';
  if (headline.includes('validation')) {
    if (fieldFromServerDetail(serverDetail) === 'email') return 'Enter a valid email address, like name@example.com.';
    return 'That email or password isn\'t correct. Check both and try again, or use "Forgot password".';
  }
  return 'We couldn\'t log you in just now. Please try again in a moment. If it keeps happening, contact support.';
};
